/**
 * Integration Secrets Management Component
 *
 * Allows administrators to view and manage API keys and credentials
 * for external service integrations (Anthropic, OpenAI, SMTP, etc.)
 *
 * Part of Phase 2.5 - Secrets Management
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuthFetch } from '../../hooks/useAuthFetch';
import {
  CheckCircleIcon,
  AlertIcon,
  RefreshIcon,
  KeyIcon,
  EyeIcon,
  EyeOffIcon,
} from '../common/Icons';

interface SecretInfo {
  key: string;
  description: string;
  path: string;
  has_value: boolean;
  last_updated: string | null;
  provider: string;
}

interface ProviderInfo {
  provider: string;
  backend: string;
  healthy: boolean;
  is_read_only: boolean;
}

interface TestResult {
  status: 'valid' | 'invalid' | 'untested';
  key: string;
  message?: string;
}

export const IntegrationSecrets: React.FC = () => {
  const [secrets, setSecrets] = useState<SecretInfo[]>([]);
  const [providerInfo, setProviderInfo] = useState<ProviderInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [newValue, setNewValue] = useState('');
  const [showValue, setShowValue] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [testing, setTesting] = useState<string | null>(null);
  const [testResults, setTestResults] = useState<Record<string, TestResult>>({});
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const { authFetch, isCancelled } = useAuthFetch();

  const loadSecrets = useCallback(async () => {
    try {
      setError(null);
      const response = await authFetch('/api/admin/secrets/integrations');
      if (isCancelled(response)) return;

      if (!response.ok) {
        throw new Error('Failed to load secrets');
      }

      const data = await response.json();
      setSecrets(data);
    } catch (err) {
      console.error('Failed to load secrets:', err);
      setError('Failed to load integration secrets. Please try again.');
    }
  }, [authFetch, isCancelled]);

  const loadProviderInfo = useCallback(async () => {
    try {
      const response = await authFetch('/api/admin/secrets/provider');
      if (isCancelled(response)) return;

      if (response.ok) {
        const data = await response.json();
        setProviderInfo(data);
      }
    } catch (err) {
      console.error('Failed to load provider info:', err);
    }
  }, [authFetch, isCancelled]);

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([loadSecrets(), loadProviderInfo()]);
      setLoading(false);
    };
    loadAll();
  }, [loadSecrets, loadProviderInfo]);

  const handleUpdate = async (key: string) => {
    if (!newValue.trim()) {
      return;
    }

    setUpdating(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const response = await authFetch(`/api/admin/secrets/integrations/${key}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value: newValue }),
      });

      if (isCancelled(response)) return;

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to update secret');
      }

      const result = await response.json();
      setSuccessMessage(result.message || 'Secret updated successfully');
      setEditingKey(null);
      setNewValue('');
      setShowValue(false);

      // Clear any previous test result for this key
      setTestResults((prev) => {
        const updated = { ...prev };
        delete updated[key];
        return updated;
      });

      // Reload secrets to reflect the change
      await loadSecrets();
    } catch (err) {
      console.error('Failed to update secret:', err);
      setError(err instanceof Error ? err.message : 'Failed to update secret');
    } finally {
      setUpdating(false);
    }
  };

  const handleTest = async (key: string) => {
    setTesting(key);
    setError(null);

    try {
      const response = await authFetch(`/api/admin/secrets/integrations/${key}/test`, {
        method: 'POST',
      });

      if (isCancelled(response)) return;

      if (!response.ok) {
        throw new Error('Failed to test secret');
      }

      const result: TestResult = await response.json();
      setTestResults((prev) => ({ ...prev, [key]: result }));
    } catch (err) {
      console.error('Failed to test secret:', err);
      setTestResults((prev) => ({
        ...prev,
        [key]: { status: 'invalid', key, message: 'Test request failed' },
      }));
    } finally {
      setTesting(null);
    }
  };

  const handleCancelEdit = () => {
    setEditingKey(null);
    setNewValue('');
    setShowValue(false);
    setError(null);
  };

  const getStatusBadge = (secret: SecretInfo) => {
    const testResult = testResults[secret.key];

    if (testResult) {
      if (testResult.status === 'valid') {
        return (
          <span
            className="badge badge-success"
            title={testResult.message}
            style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
          >
            <CheckCircleIcon size={12} />
            Verified
          </span>
        );
      } else if (testResult.status === 'invalid') {
        return (
          <span
            className="badge badge-error"
            title={testResult.message}
            style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
          >
            <AlertIcon size={12} />
            Invalid
          </span>
        );
      }
    }

    return secret.has_value ? (
      <span className="badge badge-success">Configured</span>
    ) : (
      <span className="badge badge-warning">Not Set</span>
    );
  };

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 'var(--space-8)',
          color: 'var(--text-muted)',
        }}
      >
        <RefreshIcon
          size={20}
          style={{
            marginRight: 'var(--space-2)',
            animation: 'spin 1s linear infinite',
          }}
        />
        Loading secrets...
      </div>
    );
  }

  return (
    <div className="integration-secrets" style={{ maxWidth: '800px' }}>
      {/* Header */}
      <div style={{ marginBottom: 'var(--space-6)' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-3)',
            marginBottom: 'var(--space-2)',
          }}
        >
          <KeyIcon size={24} style={{ color: 'var(--accent-primary)' }} />
          <h3
            style={{
              fontSize: 'var(--text-lg)',
              fontWeight: 600,
              margin: 0,
            }}
          >
            Integration Secrets
          </h3>
        </div>
        <p
          style={{
            fontSize: 'var(--text-sm)',
            color: 'var(--text-secondary)',
            margin: 0,
          }}
        >
          Manage API keys and credentials for external service integrations.
        </p>
      </div>

      {/* Provider Info */}
      {providerInfo && (
        <div
          className="card"
          style={{
            marginBottom: 'var(--space-6)',
            padding: 'var(--space-4)',
            background: 'var(--glass-bg)',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 'var(--space-2)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
              <div>
                <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
                  Provider
                </span>
                <div style={{ fontWeight: 500 }}>{providerInfo.provider}</div>
              </div>
              <div>
                <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
                  Backend
                </span>
                <div style={{ fontWeight: 500 }}>{providerInfo.backend}</div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
              {providerInfo.healthy ? (
                <span
                  className="badge badge-success"
                  style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                >
                  <CheckCircleIcon size={12} />
                  Healthy
                </span>
              ) : (
                <span
                  className="badge badge-error"
                  style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                >
                  <AlertIcon size={12} />
                  Unhealthy
                </span>
              )}
              {providerInfo.is_read_only && (
                <span className="badge badge-warning">Read Only</span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Messages */}
      {error && (
        <div
          style={{
            marginBottom: 'var(--space-4)',
            padding: 'var(--space-4)',
            background: 'var(--error-muted)',
            borderRadius: 'var(--radius-lg)',
            color: 'var(--error)',
            fontSize: 'var(--text-sm)',
          }}
        >
          {error}
        </div>
      )}

      {successMessage && (
        <div
          style={{
            marginBottom: 'var(--space-4)',
            padding: 'var(--space-4)',
            background: 'var(--success-muted)',
            borderRadius: 'var(--radius-lg)',
            color: 'var(--success)',
            fontSize: 'var(--text-sm)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
          }}
        >
          <CheckCircleIcon size={16} />
          {successMessage}
        </div>
      )}

      {/* Secrets List */}
      <div className="card card-elevated">
        <div
          className="table-container"
          style={{ background: 'transparent', border: 'none' }}
        >
          <table className="table">
            <thead>
              <tr>
                <th>Service</th>
                <th>Status</th>
                <th>Provider</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {secrets.map((secret) => (
                <tr key={secret.key}>
                  <td>
                    <div style={{ fontWeight: 500, marginBottom: '2px' }}>
                      {secret.description}
                    </div>
                    <code
                      style={{
                        fontSize: 'var(--text-xs)',
                        color: 'var(--text-muted)',
                        fontFamily: 'var(--font-mono)',
                      }}
                    >
                      {secret.key}
                    </code>
                  </td>
                  <td>{getStatusBadge(secret)}</td>
                  <td style={{ color: 'var(--text-secondary)' }}>{secret.provider}</td>
                  <td>
                    {editingKey === secret.key ? (
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 'var(--space-2)',
                          minWidth: '200px',
                        }}
                      >
                        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                          <div style={{ position: 'relative', flex: 1 }}>
                            <input
                              type={showValue ? 'text' : 'password'}
                              className="input"
                              value={newValue}
                              onChange={(e) => setNewValue(e.target.value)}
                              placeholder="Enter new value..."
                              style={{
                                fontSize: 'var(--text-sm)',
                                paddingRight: '36px',
                              }}
                              disabled={updating}
                              autoFocus
                            />
                            <button
                              type="button"
                              onClick={() => setShowValue(!showValue)}
                              style={{
                                position: 'absolute',
                                right: '8px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                background: 'none',
                                border: 'none',
                                padding: '4px',
                                cursor: 'pointer',
                                color: 'var(--text-muted)',
                              }}
                              title={showValue ? 'Hide value' : 'Show value'}
                            >
                              {showValue ? (
                                <EyeOffIcon size={16} />
                              ) : (
                                <EyeIcon size={16} />
                              )}
                            </button>
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={() => handleUpdate(secret.key)}
                            disabled={updating || !newValue.trim()}
                          >
                            {updating ? 'Saving...' : 'Save'}
                          </button>
                          <button
                            className="btn btn-ghost btn-sm"
                            onClick={handleCancelEdit}
                            disabled={updating}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => {
                            setEditingKey(secret.key);
                            setNewValue('');
                            setError(null);
                            setSuccessMessage(null);
                          }}
                          disabled={providerInfo?.is_read_only}
                          title={
                            providerInfo?.is_read_only
                              ? 'Provider is read-only'
                              : undefined
                          }
                        >
                          {secret.has_value ? 'Update' : 'Set'}
                        </button>
                        {secret.has_value && (
                          <button
                            className="btn btn-ghost btn-sm"
                            onClick={() => handleTest(secret.key)}
                            disabled={testing === secret.key}
                          >
                            {testing === secret.key ? (
                              <RefreshIcon
                                size={14}
                                style={{ animation: 'spin 1s linear infinite' }}
                              />
                            ) : (
                              'Test'
                            )}
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Security Note */}
      <div
        style={{
          marginTop: 'var(--space-6)',
          padding: 'var(--space-4)',
          background: 'var(--warning-muted)',
          borderRadius: 'var(--radius-lg)',
          fontSize: 'var(--text-sm)',
          color: 'var(--warning)',
        }}
      >
        <strong>Security Note:</strong> Secret values are never displayed after being
        saved. All changes are logged for audit purposes.
      </div>

      {/* Add spin animation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default IntegrationSecrets;
